import {List} from 'immutable';
import {Seconds} from '@store/@types/util';

export enum TRACK_HEIGHT {
  MINI = 8,
  DEFAULT = 20,
}

export interface CallAnnotation {
  startTime: Seconds;
  endTime: Seconds;
}

export interface Bucket<T extends CallAnnotation> {
  index: number;
  end: number;
  comments: List<T>;
}

export function buildCommentBuckets<T extends CallAnnotation>(
  comments: List<T>,
  trackHeight: TRACK_HEIGHT
) {
  const buckets = comments
    .sortBy(c => c.startTime)
    .reduce((buckets, comment) => bucketComment(buckets, comment), List<Bucket<T>>());
  const bucketCount = buckets.isEmpty() ? 1 : buckets.size;
  return {
    bucketedComments: buckets.flatMap(bucket =>
      bucket.comments.map(comment => ({index: bucket.index, comment}))
    ),
    trackHeight: bucketCount * trackHeight,
  };
}

/**
 * Adds the comment to the bucket with the smallest gap created, or if
 * there is no space, then create a new bucket for the comment.
 *
 * The size of the gaps is determined by keeping track of the end of the
 * latest comment for each bucket and comparing that to the start time of the
 * comment being inserted
 */
function bucketComment<T extends CallAnnotation>(buckets: List<Bucket<T>>, comment: T) {
  // Non-region comments go to the first bucket. Ignore these end times when calculating buckets.
  if (comment.startTime === comment.endTime) {
    return buckets.update(0, bucket => {
      if (bucket) {
        return {...bucket, comments: bucket.comments.push(comment)};
      } else {
        return {index: 0, end: 0, comments: List([comment])};
      }
    });
  }

  const bucket = buckets
    // Filter buckets we overlap on.
    .filter(bucket => comment.startTime > bucket.end)
    // Find the bucket with the smallest gap that we fit in.
    .minBy(bucket => comment.endTime - bucket.end);
  if (!bucket) {
    // No bucket found means we need a new bucket.
    return buckets.push({
      end: comment.endTime,
      index: buckets.size,
      comments: List([comment]),
    });
  } else {
    // Otherwise add to the bucket we found.
    return buckets.set(bucket.index, {
      index: bucket.index,
      end: comment.endTime,
      comments: bucket.comments.push(comment),
    });
  }
}
